<template>
  <div class="dict-manager">
    <!-- 操作栏 -->
    <div class="action-bar">
      <Button type="primary" icon="md-add" @click="handleAdd">
        新增设备
      </Button>
      <div class="search-box">
        <Input 
          v-model="searchText" 
          placeholder="搜索设备ID或名称..." 
          search 
          clearable
          style="width: 300px"
        />
      </div>
    </div>
    
    <!-- 数据表格 -->
    <div class="table-wrapper">
      <Table 
        :columns="columns" 
        :data="filteredData" 
        :loading="loading"
        stripe
        size="large"
      >
        <template slot-scope="{ row }" slot="action">
          <div class="action-buttons">
            <Button type="info" size="small" @click="handleEdit(row)" class="action-btn edit-btn">
              <Icon type="md-create" />
              <span>编辑</span>
            </Button>
            <Button type="error" size="small" @click="handleDelete(row)" class="action-btn delete-btn">
              <Icon type="md-trash" />
              <span>删除</span>
            </Button>
          </div>
        </template>
      </Table>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      :mask-closable="false"
      width="600"
      class-name="dict-modal"
    >
      <Form ref="form" :model="formData" :rules="formRules" :label-width="120">
        <FormItem label="设备ID" prop="deviceId">
          <Input 
            v-model="formData.deviceId" 
            placeholder="请输入设备ID编码（唯一）"
            :disabled="isEdit"
          />
        </FormItem>
        <FormItem label="设备中文名称" prop="deviceNameCn">
          <Input v-model="formData.deviceNameCn" placeholder="请输入设备中文名称" />
        </FormItem>
        <FormItem label="设备英文名称" prop="deviceNameEn">
          <Input v-model="formData.deviceNameEn" placeholder="请输入设备英文名称" />
        </FormItem>
        <FormItem label="设备类型" prop="deviceType">
          <Select v-model="formData.deviceType" placeholder="请选择设备类型">
            <Option :value="1">机器人核心设备</Option>
            <Option :value="2">其他设备</Option>
          </Select>
        </FormItem>
      </Form>
      
      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit" :loading="submitting">
          {{ isEdit ? '保存' : '新增' }}
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import api from '@/api/index.js'

export default {
  name: 'DeviceDict',
  data() {
    return {
      searchText: '',
      loading: false,
      tableData: [],
      modalVisible: false,
      submitting: false,
      isEdit: false,
      formData: {
        id: null,
        deviceId: '',
        deviceNameCn: '',
        deviceNameEn: '',
        deviceType: null
      },
      formRules: {
        deviceId: [
          { required: true, message: '请输入设备ID', trigger: 'blur' }
        ],
        deviceNameCn: [
          { required: true, message: '请输入设备中文名称', trigger: 'blur' }
        ],
        deviceNameEn: [
          { required: true, message: '请输入设备英文名称', trigger: 'blur' }
        ],
        deviceType: [
          { required: true, type: 'number', message: '请选择设备类型', trigger: 'change' }
        ]
      },
      columns: [
        {
          title: 'ID',
          key: 'id',
          width: 80,
          align: 'center'
        },
        {
          title: '设备ID',
          key: 'deviceId',
          minWidth: 150
        },
        {
          title: '设备中文名称',
          key: 'deviceNameCn',
          minWidth: 150
        },
        {
          title: '设备英文名称',
          key: 'deviceNameEn',
          minWidth: 150
        },
        {
          title: '设备类型',
          key: 'deviceType',
          width: 150,
          align: 'center',
          render: (h, params) => {
            const type = params.row.deviceType
            const typeText = type === 1 ? '机器人核心设备' : type === 2 ? '其他设备' : '未知'
            const color = type === 1 ? '#ff9900' : type === 2 ? '#4169e1' : '#999'
            return h('span', {
              style: {
                color: color,
                fontWeight: '600'
              }
            }, typeText)
          }
        },
        {
          title: '操作',
          slot: 'action',
          width: 200,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    modalTitle() {
      return this.isEdit ? '编辑设备字典' : '新增设备字典'
    },
    filteredData() {
      if (!this.searchText) {
        return this.tableData
      }
      const text = this.searchText.toLowerCase()
      return this.tableData.filter(item => 
        item.deviceId.toLowerCase().includes(text) ||
        item.deviceNameCn.includes(text) ||
        item.deviceNameEn.toLowerCase().includes(text)
      )
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      api.getData('system-server/monitor/dict/device/list', this).then(res => {
        this.loading = false
        if (res.state != 0) {
          this.tableData = res.data || []
          this.$emit('count-change', this.tableData.length)
        } else {
          this.$Message.error(res.msg || '加载数据失败')
        }
      }).catch(() => {
        this.loading = false
        this.$Message.error('加载数据失败')
      })
    },
    
    handleAdd() {
      this.isEdit = false
      this.formData = {
        id: null,
        deviceId: '',
        deviceNameCn: '',
        deviceNameEn: '',
        deviceType: null
      }
      this.modalVisible = true
      this.$nextTick(() => {
        this.$refs.form.resetFields()
      })
    },
    
    handleEdit(row) {
      this.isEdit = true
      this.modalVisible = true
      this.$nextTick(() => {
        // Clear validation errors first
        this.$refs.form.resetFields()
        // Then set the form data
        this.formData = { ...row }
      })
    },
    
    handleDelete(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除设备 "${row.deviceNameCn}" 吗？删除后无法恢复。`,
        onOk: () => {
        api.postData(`system-server/monitor/dict/device/delete?id=${row.id}`, {}, this).then(res => {
            if (res.state != 0) {
              this.$Message.success('删除成功')
              this.loadData()
            } else {
              this.$Message.error(res.msg || '删除失败')
            }
          }).catch(() => {
            this.$Message.error('删除失败')
          })
        }
      })
    },
    
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        
        this.submitting = true
        const data = { ...this.formData }
        if (!this.isEdit) {
          delete data.id
        }
        
        api.postData('system-server/monitor/dict/device/save', data, this).then(res => {
          this.submitting = false
          if (res.state != 0) {
            this.$Message.success(this.isEdit ? '保存成功' : '新增成功')
            this.modalVisible = false
            this.loadData()
          } else {
            this.$Message.error(res.msg || '操作失败')
          }
        }).catch(() => {
          this.submitting = false
          this.$Message.error('操作失败')
        })
        })
      }
    }
  }
</script>

<style lang="less" scoped>
@import './dict-common.less';
</style>
